<template>
  <div class="register-page">
    <div class="register-card">
      <img src="/img/logopriadi_blue.png" class="register-logo" alt="PRiADI logo" />

      <h1 class="register-title">{{ $t('register.title') }}</h1>
      <p class="register-subtitle" v-if="step === 1">{{ $t('register.step1Subtitle') }}</p>
      <p class="register-subtitle" v-if="step === 2">{{ $t('register.step2Subtitle') }}</p>

      <div class="register-steps">
        <span class="register-step-indicator active"></span>
        <span class="register-step-indicator" :class="{ active: step === 2 }"></span>
      </div>

      <!-- Step 1: Personal info -->
      <template v-if="step === 1">
        <form @submit.prevent="nextStep">
          <div class="mb-3">
            <label class="form-label-priadi" for="regFullname">{{ $t('fullname') }}</label>
            <input
              type="text"
              v-model="nama_lengkap"
              :placeholder="$t('fullnameHint')"
              class="form-control-priadi"
              id="regFullname" />
            <p class="form-error-priadi" v-if="errors['nama_lengkap']">{{ errors['nama_lengkap'] }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label-priadi" for="regBirthdate">{{ $t('birthdate') }}</label>
            <input
              type="date"
              v-model="tgl_lahir"
              :placeholder="$t('birthdateHint')"
              class="form-control-priadi"
              id="regBirthdate" />
            <p class="form-error-priadi" v-if="errors['tgl_lahir']">{{ errors['tgl_lahir'] }}</p>
          </div>

          <div class="mb-4">
            <label class="form-label-priadi" for="regPhone">{{ $t('phone') }}</label>
            <input
              type="tel"
              v-model="nomor_handphone"
              :placeholder="$t('phoneHint')"
              class="form-control-priadi"
              id="regPhone" />
            <p class="form-error-priadi" v-if="errors['nomor_handphone']">{{ errors['nomor_handphone'] }}</p>
          </div>

          <button class="btn-priadi btn-priadi-lg btn-priadi-block" type="submit">
            {{ $t('continue') }}
          </button>
        </form>
      </template>

      <!-- Step 2: Account info -->
      <template v-if="step === 2">
        <form @submit.prevent="submitForm">
          <div class="mb-3">
            <label class="form-label-priadi" for="regEmail">{{ $t('email') }}</label>
            <input
              type="email"
              v-model="alamat_email"
              :placeholder="$t('emailHint')"
              class="form-control-priadi"
              id="regEmail" />
            <p class="form-error-priadi" v-if="errors['alamat_email']">{{ errors['alamat_email'] }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label-priadi" for="regPassword">{{ $t('password') }}</label>
            <div class="position-relative">
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="kata_sandi"
                :placeholder="$t('passwordHint')"
                class="form-control-priadi"
                id="regPassword" />
              <button
                type="button"
                class="register-eye"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? 'Hide password' : 'Show password'">
                <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'" aria-hidden="true"></i>
              </button>
            </div>
            <p class="form-error-priadi" v-if="errors['kata_sandi']">{{ errors['kata_sandi'] }}</p>
          </div>

          <div class="mb-3">
            <label class="form-label-priadi" for="regPasswordConfirm">{{ $t('rePassword') }}</label>
            <div class="position-relative">
              <input
                :type="showPasswordConfirm ? 'text' : 'password'"
                v-model="kata_sandi_confirm"
                :placeholder="$t('rePasswordHint')"
                class="form-control-priadi"
                id="regPasswordConfirm" />
              <button
                type="button"
                class="register-eye"
                @click="showPasswordConfirm = !showPasswordConfirm"
                :aria-label="showPasswordConfirm ? 'Hide password' : 'Show password'">
                <i class="bi" :class="showPasswordConfirm ? 'bi-eye-slash' : 'bi-eye'" aria-hidden="true"></i>
              </button>
            </div>
            <p class="form-error-priadi" v-if="errors['kata_sandi_confirm']">{{ errors['kata_sandi_confirm'] }}</p>
          </div>

          <div class="register-terms mb-4">
            <input
              class="form-check-input"
              type="checkbox"
              id="regAgree"
              v-model="isAgree"
              required />
            <label class="register-terms-label" for="regAgree">
              {{ $t('agreeTerms') }}
              <a
                href="https://priadi.id/en/non-disclosure-agreement"
                target="_blank"
                rel="noopener noreferrer"
                class="register-terms-link">
                {{ $t('termsCondition') }}
              </a>
            </label>
          </div>

          <button
            class="btn-priadi btn-priadi-lg btn-priadi-block mb-3"
            type="submit"
            :disabled="isLoading || !isAgree">
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            {{ $t('signup') }}
          </button>

          <button
            class="btn-priadi-outline btn-priadi-lg btn-priadi-block"
            type="button"
            @click="step = 1">
            {{ $t('confirmation.back') }}
          </button>
        </form>
      </template>

      <p class="register-footer">
        {{ $t('hasAccount') }}
        <router-link to="/auth" class="register-link">{{ $t('signin') }}</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'Register',
  data() {
    return {
      step: 1,
      errors: {},
      nama_lengkap: '',
      alamat_email: '',
      kata_sandi: '',
      kata_sandi_confirm: '',
      showPassword: false,
      showPasswordConfirm: false,
      tgl_lahir: '',
      nomor_handphone: '',
      isAgree: false,
      isLoading: false,
    }
  },
  methods: {
    nextStep() {
      this.errors = {}

      this.errors['nama_lengkap'] =
        this.nama_lengkap.length < 2 ? this.$t('validation.minLength', { field: this.$t('fullname'), min: 2 }) : ''

      this.errors['tgl_lahir'] = !this.tgl_lahir
        ? this.$t('validation.required', { field: this.$t('birthdate') })
        : ''

      this.errors['nomor_handphone'] = !this.nomor_handphone
        ? this.$t('validation.required', { field: this.$t('phone') })
        : ''

      if (
        !this.errors['nama_lengkap'] &&
        !this.errors['tgl_lahir'] &&
        !this.errors['nomor_handphone']
      ) {
        this.step = 2
      }
    },
    async submitForm() {
      this.errors = {}

      this.errors['alamat_email'] =
        this.alamat_email.length < 3 ? this.$t('validation.minLength', { field: this.$t('email'), min: 3 }) : ''

      this.errors['kata_sandi'] =
        this.kata_sandi.length < 8 ? this.$t('validation.minLength', { field: this.$t('password'), min: 8 }) : ''

      this.errors['kata_sandi_confirm'] =
        this.kata_sandi_confirm !== this.kata_sandi ? this.$t('validation.passwordMismatch') : ''

      if (
        !this.errors['alamat_email'] &&
        !this.errors['kata_sandi'] &&
        !this.errors['kata_sandi_confirm']
      ) {
        this.isLoading = true
        try {
          const formData = {
            fullname: this.nama_lengkap,
            email: this.alamat_email,
            password: this.kata_sandi,
            birthdate: this.tgl_lahir,
            phone_number: this.nomor_handphone,
          }

          const response = await axios.post('/api/register', formData)
          if (response.data.message) {
            Swal.fire({
              title: this.$t('success'),
              icon: 'success',
              text: this.$t('redirectLogin'),
              timer: 2000,
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading()
              },
            }).then((result) => {
              if (result.dismiss === Swal.DismissReason.timer) {
                this.$router.push('/auth')
              }
            })
          }
        } catch (error) {
          console.error('Registration error:', error)
          Swal.fire({
            icon: 'error',
            title: this.$t('error'),
            text: error.response?.data?.detail || this.$t('error'),
          })
        } finally {
          this.isLoading = false
        }
      }
    },
  },
}
</script>

<style scoped>
.register-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: linear-gradient(
      160deg,
      rgba(var(--color-primary-rgb), 0.88) 0%,
      rgba(var(--color-primary-rgb), 0.72) 100%
    ),
    url('/img/login.png');
  background-size: cover;
  background-position: center;
  padding: var(--space-6);
}

.register-card {
  width: 100%;
  max-width: 400px;
  background-color: var(--color-surface);
  border-radius: var(--radius-lg);
  padding: var(--space-8) var(--space-6);
  box-shadow: var(--shadow-lg);
}

.register-logo {
  display: block;
  width: 80px;
  margin: 0 auto var(--space-4);
}

.register-steps {
  display: flex;
  justify-content: center;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
}

.register-step-indicator {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background-color: var(--color-border);
  transition: background-color var(--transition-base), width var(--transition-base);
}

.register-step-indicator.active {
  background-color: var(--color-primary);
  width: 24px;
}

.register-title {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--color-text-primary);
  text-align: center;
  margin-bottom: var(--space-1);
}

.register-subtitle {
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: var(--space-6);
}

.register-terms {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
}

.register-terms .form-check-input {
  margin-top: 0.2em;
  flex-shrink: 0;
}

.register-terms-label {
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  line-height: var(--leading-normal);
}

.register-terms-link {
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  text-decoration: underline;
}

.register-terms-link:hover {
  color: var(--color-primary-hover);
}

.register-footer {
  text-align: center;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  margin-top: var(--space-6);
  margin-bottom: 0;
}

.register-link {
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  text-decoration: none;
}

.register-link:hover {
  color: var(--color-primary-hover);
  text-decoration: underline;
}

.register-eye {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  padding: var(--space-2) var(--space-3);
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: color var(--transition-base);
}

.register-eye:hover {
  color: var(--color-text-primary);
}
</style>
